import axios from 'axios';

// The GPL registration API is a separate service from the Linkway backend.
// Both the base URL and the key are per-environment (see envDir/.env.*) so a production
// build never points at QA or ships the QA key.
//
// NOTE: Create React App inlines every REACT_APP_* value into the JS bundle at build
// time, so this key is readable by anyone who opens the site. It is an identifier for
// the caller, not a secret - keep the API's own per-endpoint authorisation in mind
// before granting this key any privileged scope.
const BASE_URL = process.env.REACT_APP_GPL_BASE_URL || 'https://api.yomo.ooo/qa-api/api';
const API_KEY = process.env.REACT_APP_GPL_API_KEY;

const gplClient = axios.create({
  baseURL: BASE_URL,
  // Sent on every call, per the API contract.
  headers: API_KEY ? { 'X-Gpl-Api-Key': API_KEY } : undefined,
});

/**
 * Whether an auction is currently accepting registrations.
 * -> { male: boolean, female: boolean, open: boolean }
 */
export const fetchRegistrationWindow = async () => {
  const { data } = await gplClient.get('/Player/registration-window');
  return data;
};

/**
 * The five lookup endpoints. Each returns a flat array; `externalId` is the value
 * the POST expects, and the label field differs per endpoint.
 * Deliberately not hardcoded - these are editable from the admin panel.
 */
const LOOKUPS = [
  { key: 'roles', path: '/PlayerRole', labelField: 'roleName' },
  { key: 'battingStyles', path: '/BattingStyle', labelField: 'battingType' },
  { key: 'bowlingStyles', path: '/BowlingStyle', labelField: 'bowlingType' },
  { key: 'tShirtSizes', path: '/TshirtSize', labelField: 'size' },
  { key: 'workLocations', path: '/WorkLocation', labelField: 'name' },
];

export const fetchLookups = async () => {
  const responses = await Promise.all(
    LOOKUPS.map(({ path }) => gplClient.get(path).then((res) => res.data))
  );

  return LOOKUPS.reduce((acc, { key, labelField }, index) => {
    acc[key] = (responses[index] || []).map((item) => ({
      value: item.externalId,
      label: item[labelField],
    }));
    return acc;
  }, {});
};

/**
 * Live email availability check.
 * 200 -> free, 400 with "Email Id already Exists" -> taken.
 * The POST re-checks this anyway, so a network failure here is treated as "free".
 */
export const checkEmailAvailable = async (emailId) => {
  try {
    await gplClient.get(`/Player/Exists/${encodeURIComponent(emailId)}`);
    return { available: true };
  } catch (error) {
    if (error.response?.status === 400) {
      return { available: false, message: extractErrorText(error) || 'Email Id already Exists' };
    }
    return { available: true };
  }
};

/**
 * Submit the registration as multipart/form-data.
 * `playerImg` is the optional photo file part; every other field is flat.
 */
export const registerPlayer = async (fields, photoFile) => {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    // Skip optional fields the user left empty so the API applies its own defaults
    // rather than receiving an empty string where it expects an int.
    if (value === '' || value === null || value === undefined) return;
    formData.append(key, value);
  });

  if (photoFile) {
    formData.append('playerImg', photoFile);
  }

  const { data } = await gplClient.post('/Player', formData);
  return data;
};

/**
 * The record may arrive bare, wrapped ({ data } / { player } / { result }), or as a
 * single-element list - one email can only match one player in a season, so a list of
 * one is still just that player.
 */
const unwrapPlayer = (data) => {
  const body = data?.data ?? data?.player ?? data?.result ?? data;
  const record = Array.isArray(body) ? body[0] : body;
  return record && typeof record === 'object' ? record : null;
};

/**
 * The caller's own registration for the current season.
 *
 * This endpoint lives in the integration namespace rather than under /Player, and answers
 * in its own dialect: errors are { code, message, field } with snake_case codes
 * ("not_registered", "invalid_request") where /Player returns PascalCase model-validation
 * bodies. A missing registration is a 404 with code "not_registered" - that is an answer,
 * not a failure, so it comes back as null and the caller can tell it apart from a lookup
 * that genuinely broke (which still throws).
 */
export const fetchPlayerByEmail = async (emailId) => {
  try {
    const { data } = await gplClient.get('/integration/players', { params: { email: emailId } });
    return unwrapPlayer(data);
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }
};

/** The integration namespace's error body: { code, message, field }. */
export const extractIntegrationError = (error) => {
  const data = error?.response?.data;
  if (!data) return '';
  if (typeof data === 'string') return data.trim();
  return typeof data.message === 'string' ? data.message : '';
};

// Punctuation and case removed, so "TShirtName", "tshirtName" and "t_shirt_name" all
// collapse to one key. The integration namespace does not share /Player's PascalCase
// (its error bodies are snake_case), so the GET's casing is matched rather than assumed.
const normaliseKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, '');

// Only for fields whose GET name is not simply the POST name in another casing.
const PLAYER_FIELD_ALIASES = {
  EmailId: ['email'],
  PhoneNo: ['phone', 'phoneNumber', 'mobile', 'mobileNo'],
  Dob: ['age'],
  TshirtSizeExternalId: ['tshirtSize', 'tshirtSizeId', 'size'],
  WorkLocationExternalId: ['workLocation', 'workLocationId', 'location'],
  PlayerRoleExternalId: ['playerRole', 'playerRoleId', 'role', 'roleName'],
  BattingStyleExternalId: ['battingStyle', 'battingStyleId', 'battingType'],
  BowlingStyleExternalId: ['bowlingStyle', 'bowlingStyleId', 'bowlingType'],
  TShirtNumber: ['jerseyNumber', 'shirtNumber'],
  TShirtName: ['jerseyName', 'shirtName'],
  ExperienceYears: ['experience'],
  CricHeroesAccount: ['hasCricHeroes', 'cricHeroes'],
  CricHeroesUserId: ['cricHeroesPhone', 'cricHeroesNumber'],
  MedicalConditions: ['hasMedicalConditions'],
};

// The selects render from the lookup lists, so their values must be externalIds.
const LOOKUP_FOR_FIELD = {
  TshirtSizeExternalId: 'tShirtSizes',
  WorkLocationExternalId: 'workLocations',
  PlayerRoleExternalId: 'roles',
  BattingStyleExternalId: 'battingStyles',
  BowlingStyleExternalId: 'bowlingStyles',
};

const YES_NO_FIELDS = ['CricHeroesAccount', 'MedicalConditions'];

/**
 * A lookup-backed value may come back as the externalId, as a nested object, or as the
 * display name. Resolve all three against the option list the select actually renders,
 * because a value that is not one of its options makes MUI render an empty select.
 */
const resolveLookupValue = (raw, options = []) => {
  if (raw === null || raw === undefined || raw === '') return '';

  const candidate =
    typeof raw === 'object' ? raw.externalId ?? raw.value ?? raw.id ?? raw.name ?? '' : raw;
  const asString = String(candidate).trim();
  if (!asString) return '';

  if (options.some((option) => String(option.value) === asString)) return asString;

  const byLabel = options.find(
    (option) => String(option.label).trim().toLowerCase() === asString.toLowerCase()
  );
  return byLabel ? String(byLabel.value) : '';
};

/** The radio rows are 'Yes'/'No' strings; the API may answer with booleans. */
const toYesNo = (raw) => {
  if (raw === true) return 'Yes';
  if (raw === false) return 'No';
  const text = String(raw ?? '').trim().toLowerCase();
  if (['yes', 'true', '1', 'y'].includes(text)) return 'Yes';
  if (['no', 'false', '0', 'n'].includes(text)) return 'No';
  return '';
};

/**
 * Maps a fetched player record onto the Formik field names, for the read-only view shown
 * to someone who has already registered. `lookups` must be loaded first or every select
 * resolves to '' - see the caller, which waits for both.
 */
export const mapPlayerToFormValues = (player, lookups = {}, fieldNames = []) => {
  if (!player || typeof player !== 'object') return {};

  const byKey = Object.entries(player).reduce((acc, [key, value]) => {
    acc[normaliseKey(key)] = value;
    return acc;
  }, {});

  const read = (field) => {
    const names = [field, ...(PLAYER_FIELD_ALIASES[field] || [])].map(normaliseKey);
    const hit = names.find((name) => byKey[name] !== undefined && byKey[name] !== null);
    return hit === undefined ? undefined : byKey[hit];
  };

  return fieldNames.reduce((acc, field) => {
    const raw = read(field);
    if (raw === undefined) return acc;

    if (LOOKUP_FOR_FIELD[field]) {
      acc[field] = resolveLookupValue(raw, lookups[LOOKUP_FOR_FIELD[field]]);
    } else if (YES_NO_FIELDS.includes(field)) {
      acc[field] = toYesNo(raw);
    } else if (typeof raw === 'object') {
      // Never hand an object to a TextField - React throws on it.
      acc[field] = String(raw.name ?? raw.value ?? '');
    } else {
      acc[field] = String(raw);
    }
    return acc;
  }, {});
};

/**
 * The stored player photo, but only when it is something the browser can actually load.
 *
 * `photo` comes back as a bare storage filename ("5adf2cc1-....jpeg") with no documented
 * base path, and a relative src would resolve against the Linkway origin and render as a
 * broken image. Until that base is known, only an absolute URL is accepted and the caller
 * falls back to the employee record's own photo.
 */
export const extractPlayerPhoto = (player) => {
  if (!player || typeof player !== 'object') return '';
  const byKey = Object.entries(player).reduce((acc, [key, value]) => {
    acc[normaliseKey(key)] = value;
    return acc;
  }, {});
  const candidate = ['playerImg', 'playerImage', 'playerImageUrl', 'imageUrl', 'photoUrl', 'photo']
    .map(normaliseKey)
    .map((name) => byKey[name])
    .find((value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim()));
  return candidate ? candidate.trim() : '';
};

/**
 * The API returns errors in three shapes:
 *  - 400 JSON  : { errors: { FieldName: ["message"] }, title, status }  (model validation)
 *  - 400 text  : "Unrecognised playerRoleExternalId." / "Registration has been closed"
 *  - 409 text  : "Player with this email Id already exists." / "T-Shirt name is taken."
 * Returns plain readable text for the two string cases.
 */
export const extractErrorText = (error) => {
  const data = error?.response?.data;
  if (!data) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data.title === 'string' && !data.errors) return data.title;
  return '';
};

/**
 * Maps a 400 model-validation body onto Formik field names.
 * API field names are PascalCase and may vary in casing, so match case-insensitively.
 */
export const extractFieldErrors = (error, fieldNames) => {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};

  const lookup = fieldNames.reduce((acc, name) => {
    acc[name.toLowerCase()] = name;
    return acc;
  }, {});

  return Object.entries(errors).reduce((acc, [apiField, messages]) => {
    const formField = lookup[apiField.toLowerCase()];
    if (formField) {
      acc[formField] = Array.isArray(messages) ? messages.join(' ') : String(messages);
    }
    return acc;
  }, {});
};

export default gplClient;
